import { get, isEqual } from "lodash";
import React from "react";
import { Grid, Icon } from "semantic-ui-react";

import { ErrorPositions } from "enums/errorPositions";
import { ValidatedForm } from "../../../../../../../components/forms";
import { type SessionPayload, type SessionView } from "../../../../types/state";
import { getPropName } from "../../Utils/getSessionPropName";
import { type CommonSessionBuilderProps } from "./SingleSessionBuilder";

import { TimezoneDropdown } from "features/Library/Common/Fields/timezone-dropdown/TimezoneDropdown";
import "./singleSessionBuilderRow.scss";

import { faUserGroup } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { keyboardInterceptors } from "utils/components/inputUtils";

export interface SingleSessionBuilderRowProps extends CommonSessionBuilderProps {
  session: SessionView;
  index: number;
  onRemove: (id: number) => void;
  hideRemove?: boolean;
}

const maxRegistrationLimitLength: number = 5;

export const SingleSessionBuilderRow = (props: SingleSessionBuilderRowProps) => {
  const { session, validatedFieldProps, index, disabled, onRemove, hideRemove, onBlur } = props;
  const { touched, ...otherValidatedProps } = validatedFieldProps;

  const limitEventRegistrationEnabled = !!useFeatureFlag(FeatureFlags.LimitEventRegistration);

  return (
    <React.Fragment key={index}>
      <div className="single-session-builder-row">
        <Grid stackable columns="equal" verticalAlign="bottom" className="overridden-bottom-margin">
          <Grid.Row>
            <Grid.Column className="container-column">
              <div className="container-fields">
                <div className="session-field session-url">
                  <ValidatedForm.InputField
                    label="Event URL"
                    value={session.webinarUrl}
                    propertyName={getPropName<SessionView>("webinarUrl", index)}
                    markAsRequired
                    disabled={disabled}
                    errorPosition="bottom"
                    {...validatedFieldProps}
                    onBlur={onBlur}
                  />
                </div>
                {limitEventRegistrationEnabled && (
                  <div className="session-field session-registration-limit">
                    <ValidatedForm.InputField
                      placeholder="No limit"
                      label="Registration Limit"
                      value={session.registrationLimit}
                      propertyName={getPropName<SessionView>("registrationLimit", index)}
                      disabled={disabled}
                      errorPosition="bottom"
                      onBlur={onBlur}
                      maxLength={maxRegistrationLimitLength}
                      onKeyPress={keyboardInterceptors.integerOnly}
                      {...validatedFieldProps}
                      icon={<FontAwesomeIcon icon={faUserGroup} />}
                      iconPosition="left"
                    />
                  </div>
                )}
                {!hideRemove && (
                  <div className="session-field remove-btn">
                    <Icon
                      disabled={disabled}
                      className="minus circle icon"
                      size="big"
                      color="blue"
                      onClick={() => onRemove(index)}
                    />
                  </div>
                )}
              </div>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column className="container-column">
              <div className="container-fields">
                <div className="session-field session-datetime">
                  <ValidatedForm.DatePickerField
                    label="Start date"
                    selected={session.startDate || ""}
                    propertyName={getPropName<SessionView>("startDate", index)}
                    disabled={disabled}
                    errorPosition={ErrorPositions.bottom}
                    markAsRequired
                    {...validatedFieldProps}
                    onBlur={() => {
                      onBlur(getPropName<SessionPayload>("sessionStartDate", index));
                    }}
                  />
                </div>
                <div className="session-field session-datetime">
                  <ValidatedForm.TimePickerField
                    label="Starts at"
                    selected={session.startsAt || ""}
                    propertyName={getPropName<SessionView>("startsAt", index)}
                    markAsRequired
                    disabled={disabled}
                    errorPosition={ErrorPositions.bottom}
                    className={"single-session-builder__time-picker"}
                    {...validatedFieldProps}
                    onBlur={() => {
                      onBlur(getPropName<SessionPayload>("startsAtTicksMinutes", index));
                    }}
                  />
                </div>
                <div className="session-field session-datetime">
                  <ValidatedForm.TimePickerField
                    label="Ends at"
                    selected={session.endsAt || ""}
                    propertyName={getPropName<SessionView>("endsAt", index)}
                    markAsRequired
                    disabled={disabled}
                    errorPosition={ErrorPositions.bottom}
                    className={"single-session-builder__time-picker"}
                    {...validatedFieldProps}
                    onBlur={() => {
                      onBlur(getPropName<SessionPayload>("endsAtTicksMinutes", index));
                    }}
                  />
                </div>
                <div className="session-field session-timezone">
                  <TimezoneDropdown
                    className={"timezone-width"}
                    label="Time zone"
                    propertyName={getPropName<SessionView>("timeZone", index)}
                    value={session.timeZone || ""}
                    markAsRequired
                    disabled={disabled}
                    error=""
                    touched={false}
                    icon={<Icon className="fa-clock" />}
                    onBlur={onBlur}
                    {...otherValidatedProps}
                  />
                </div>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>
    </React.Fragment>
  );
};

/* istanbul ignore next */
const getNeededProps = (props: SingleSessionBuilderRowProps) => {
  const { arrayHelpers, validatedFieldProps, onRemove, ...rest } = props;

  const errors = get(props.validatedFieldProps.errors, `sessions.${props.index}`);
  const touched = get(props.validatedFieldProps.touched, `sessions.${props.index}`);

  return { ...rest, errors, touched };
};

/* istanbul ignore next */
export default React.memo(SingleSessionBuilderRow, (prev, next) => {
  return isEqual(getNeededProps(prev), getNeededProps(next));
});
